import type { JSONSchemaType } from '@mai/types';

/**
 * Get the type of a JSON Schema
 */
export function getSchemaType(
  schema: JSONSchemaType,
): string | string[] | undefined {
  return schema.type;
}

/**
 * Check if a field is required
 */
export function isFieldRequired(
  fieldName: string,
  required: string[] = [],
): boolean {
  return required.includes(fieldName);
}

/**
 * Generate a unique key for a field
 */
export function getFieldKey(name: string | string[]): string {
  return Array.isArray(name) ? name.join('.') : name;
}

/**
 * Get field title from schema
 */
export function getFieldTitle(
  schema: JSONSchemaType,
  fieldName: string,
): string {
  return (
    schema.title ||
    fieldName.charAt(0).toUpperCase() +
      fieldName
        .slice(1)
        .replace(/([A-Z])/g, ' $1')
        .trim()
  );
}

/**
 * Get field description from schema
 */
export function getFieldDescription(
  schema: JSONSchemaType,
): string | undefined {
  return schema.description;
}

/**
 * Check if a schema has enum values
 */
export function hasEnumValues(schema: JSONSchemaType): boolean {
  return Array.isArray(schema.enum) && schema.enum.length > 0;
}

/**
 * Get enum options for select fields
 */
export function getEnumOptions(
  schema: JSONSchemaType,
): Array<{ label: string; value: unknown }> {
  if (!hasEnumValues(schema)) return [];

  return schema.enum!.map((value) => ({
    label: typeof value === 'string' ? value : JSON.stringify(value),
    value,
  }));
}

/**
 * Format validation rules based on JSON Schema constraints
 */
export function getValidationRules(
  schema: JSONSchemaType,
  required: boolean,
  fieldName: string,
) {
  const rules: Array<Record<string, unknown>> = [];

  if (required) {
    rules.push({
      required: true,
      message: `${getFieldTitle(schema, fieldName)} is required`,
    });
  }

  const type = getSchemaType(schema);

  if (!type) return rules;

  switch (type) {
    case 'string':
      if (schema.minLength !== undefined) {
        rules.push({
          min: schema.minLength,
          message: `Minimum length is ${schema.minLength}`,
        });
      }
      if (schema.maxLength !== undefined) {
        rules.push({
          max: schema.maxLength,
          message: `Maximum length is ${schema.maxLength}`,
        });
      }
      if (schema.pattern) {
        rules.push({
          pattern: new RegExp(schema.pattern),
          message: 'Invalid format',
        });
      }
      if (schema.format === 'email') {
        rules.push({ type: 'email', message: 'Invalid email address' });
      }
      if (schema.format === 'url') {
        rules.push({ type: 'url', message: 'Invalid URL' });
      }
      break;

    case 'number':
    case 'integer':
      rules.push({ type: 'number', message: 'Must be a number' });
      if (schema.minimum !== undefined) {
        rules.push({
          validator: (_: unknown, value: number) => {
            if (value < schema.minimum!) {
              return Promise.reject(`Minimum value is ${schema.minimum}`);
            }
            return Promise.resolve();
          },
        });
      }
      if (schema.maximum !== undefined) {
        rules.push({
          validator: (_: unknown, value: number) => {
            if (value > schema.maximum!) {
              return Promise.reject(`Maximum value is ${schema.maximum}`);
            }
            return Promise.resolve();
          },
        });
      }
      if (schema.multipleOf !== undefined) {
        rules.push({
          validator: (_: unknown, value: number) => {
            if (value % schema.multipleOf! !== 0) {
              return Promise.reject(
                `Must be a multiple of ${schema.multipleOf}`,
              );
            }
            return Promise.resolve();
          },
        });
      }
      break;

    case 'array':
      if (schema.minItems !== undefined) {
        rules.push({
          validator: (_: unknown, value: unknown[]) => {
            if (!value || value.length < schema.minItems!) {
              return Promise.reject(
                `Minimum ${schema.minItems} items required`,
              );
            }
            return Promise.resolve();
          },
        });
      }
      if (schema.maxItems !== undefined) {
        rules.push({
          validator: (_: unknown, value: unknown[]) => {
            if (value && value.length > schema.maxItems!) {
              return Promise.reject(`Maximum ${schema.maxItems} items allowed`);
            }
            return Promise.resolve();
          },
        });
      }
      if (schema.uniqueItems) {
        rules.push({
          validator: (_: unknown, value: unknown[]) => {
            if (value && new Set(value).size !== value.length) {
              return Promise.reject('Items must be unique');
            }
            return Promise.resolve();
          },
        });
      }
      break;
  }

  return rules;
}

/**
 * Determine the input type based on schema
 */
export function getInputType(schema: JSONSchemaType): string {
  const type = getSchemaType(schema);

  if (hasEnumValues(schema)) {
    return 'select';
  }

  switch (type) {
    case 'string':
      if (schema.format === 'date') return 'date';
      if (schema.format === 'date-time') return 'datetime';
      if (schema.format === 'time') return 'time';
      if (schema.format === 'email') return 'email';
      if (schema.format === 'uri' || schema.format === 'url') return 'url';
      if (schema.format === 'password') return 'password';
      if (schema.maxLength && schema.maxLength > 100) return 'textarea';
      return 'text';

    case 'number':
    case 'integer':
      return 'number';

    case 'boolean':
      return 'switch';

    case 'array':
      return 'array';

    case 'object':
      return 'object';

    default:
      return 'text';
  }
}
